import React, { FC, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FormattedMessage, useIntl } from "react-intl";

import "./PickupDeliveryProofDialog.scss";
import { submitPOD, submitPOP } from "api";
import Modal from "components/v2/Modal/Modal";
import Tabs from "components/v2/Tabs/Tabs";
import Tab from "components/v2/Tabs/components/Tab";
import usePermissions from "hooks/usePermissions";
import { invoiceQueryKeys } from "api/queries/invoice/invoiceQueryKeys";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import Button from "components/v2/Button/Button";
import errorToast from "helpers/errorToast";
import ProofOfPickup from "./components/ProofOfPickup";
import ProofOfDelivery from "./components/ProofOfDelivery";
import { getDelayReasons, isPodAttachmentsValid } from "./utils/podFormUtils";
import { markShipmentQueryDelivered } from "./podBucketAttachmentHelpers";

type PickupDeliveryProofDialogProps = {
  show: boolean;
  onHide: () => void;
  isPoPSubmitted: boolean;
  isOffPlatform: boolean;
  setIsShipmentDelivered?: React.Dispatch<React.SetStateAction<boolean>>;
  refetchFn?: Function;
  mode?: "default" | "pickupOnly";
};

const PickupDeliveryProofDialog: FC<PickupDeliveryProofDialogProps> = function (props) {
  const { show, onHide, isPoPSubmitted, isOffPlatform, refetchFn, setIsShipmentDelivered, mode = "default" } = props;
  const isPickupOnly = mode === "pickupOnly";

  const intl = useIntl();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("pickup");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { id: shipmentId } = useParams();
  const { permissions } = usePermissions();
  const { full_name, user_type } = JSON.parse(permissions || "{}");
  const methods: any = useForm({
    defaultValues: {
      file_attachments: [],
    },
  });
  const { handleSubmit } = methods;

  const onSubmit = () => {
    if (isPickupOnly) {
      handlePopSubmit();
      return;
    }

    if (user_type !== "shipper" && !isPoPSubmitted && !isOffPlatform) {
      if (activeTab === "pickup") {
        handlePopSubmit();
      }
      if (activeTab === "pickup_and_delivery") {
        handlePodSubmit({ ...getPopPayload(), ...getPodPayload() });
      }
      if (activeTab === "delivery") {
        handlePodSubmit(getPodPayload());
      }
    } else {
      handlePodSubmit(getPodPayload());
    }
  };

  const getPopPayload = () => {
    const formData: any = methods.getValues();
    return {
      acceptor_name: full_name,
      pickup_date: formData.pickup_date,
      pickup_time: formData.pickup_time,
      ...(isOffPlatform && { pickup_timezone: formData.pickup_timezone }),
    };
  };

  const getPodPayload = () => {
    const formData: any = methods.getValues();
    const delayReasons = getDelayReasons(formData.delay_reason);
    return {
      acceptor_name: formData.acceptor_name,
      delivery_date: formData.delivery_date,
      delivery_time: formData.delivery_time,
      billing_status: formData.billing_status,
      file_attachments: formData.file_attachments,
      ...(isOffPlatform && { delivery_timezone: formData.delivery_timezone }),
      ...(delayReasons && { delay_reason: delayReasons }),
    };
  };

  const handlePopSubmit = () => {
    setIsSubmitting(true);
    const payload = {
      ...getPopPayload(),
    };
    submitPOP(shipmentId, payload)
      .then(() => {
        if (refetchFn) refetchFn();
        onHide();
      })
      .catch((err) => {
        errorToast(err);
      })
      .finally(() => setIsSubmitting(false));
  };

  const handlePodSubmit = (payload: any) => {
    if (!isPodAttachmentsValid(methods.getValues())) {
      return;
    }

    setIsSubmitting(true);

    submitPOD(shipmentId, payload)
      .then(() => {
        if (shipmentId) {
          queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.podBucketAttachments(shipmentId) });
          queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.shipmentInvoicesListing(shipmentId) });
          queryClient.setQueryData(shipmentQueryKeys.shipmentDetails(shipmentId), markShipmentQueryDelivered);
          queryClient.setQueryData(shipmentQueryKeys.shipmentBasicDetails(shipmentId), markShipmentQueryDelivered);
        }
        toast(
          intl.formatMessage({
            id: "components.molecules.top-nav.deliveryCompleted",
            defaultMessage: "Delivery marked as complete",
          }),
          { type: "success" },
        );
        if (setIsShipmentDelivered) setIsShipmentDelivered(true);
        setTimeout(() => {
          if (refetchFn) refetchFn();
        }, 3000);
        onHide();
      })
      .catch((err) => {
        errorToast(err);
      })
      .finally(() => setIsSubmitting(false));
  };

  const getDialogTitle = () => {
    if (isPickupOnly) {
      return intl.formatMessage({
        id: "components.molecules.top-nav.proofOfPickup",
        defaultMessage: "Proof of pickup",
      });
    }
    if (user_type === "shipper" || isOffPlatform || isPoPSubmitted) {
      return intl.formatMessage({
        id: "components.molecules.top-nav.proofOfDelivery",
        defaultMessage: "Proof of delivery",
      });
    }
    return intl.formatMessage({
      id: "components.molecules.top-nav.proofOfPickupOrDelivery",
      defaultMessage: "Proof of pickup / delivery",
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header>
        <div className="text-primary text-xl font-semibold">{getDialogTitle()}</div>
        <div className="text-tertiary text-sm mb-2xl">
          {user_type === "shipper" ? (
            <FormattedMessage
              id="components.molecules.top-nav.notifyStatusUpdateProvider"
              defaultMessage="We will notify the provider of the status update."
            />
          ) : (
            <FormattedMessage
              id="components.molecules.top-nav.notifyStatusUpdateShipper"
              defaultMessage="We will notify the shipper of the status update."
            />
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        <FormProvider {...methods}>
          <form id="submit-pod-form">
            {isPickupOnly && (
              <div className="mt-3xl">
                <ProofOfPickup />
              </div>
            )}
            {!isPickupOnly && user_type !== "shipper" && !isPoPSubmitted && !isOffPlatform && (
              <>
                <div className="text-secondary-v2 text-sm font-medium">
                  <FormattedMessage
                    id="components.molecules.top-nav.milestoneQuestion"
                    defaultMessage="Which milestone are you submitting proof for?"
                  />
                </div>
                <div className="mt-sm">
                  <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key)} type="secondary-gray">
                    <Tab
                      eventKey="pickup"
                      title={intl.formatMessage({ id: "components.molecules.top-nav.pickup", defaultMessage: "Pickup" })}
                    >
                      <div className="mt-3xl">
                        <ProofOfPickup />
                      </div>
                    </Tab>
                    <Tab
                      eventKey="delivery"
                      title={intl.formatMessage({ id: "components.molecules.top-nav.delivery", defaultMessage: "Delivery" })}
                    >
                      <div className="mt-3xl">
                        <ProofOfDelivery />
                      </div>
                    </Tab>
                    <Tab
                      eventKey="pickup_and_delivery"
                      title={intl.formatMessage({
                        id: "components.molecules.top-nav.pickupAndDelivery",
                        defaultMessage: "Pickup and delivery",
                      })}
                    >
                      <div className="mt-3xl">
                        <ProofOfPickup />
                        <div className="mt-3xl">
                          <ProofOfDelivery />
                        </div>
                      </div>
                    </Tab>
                  </Tabs>
                </div>
              </>
            )}
            {!isPickupOnly && (user_type === "shipper" || isOffPlatform) && <ProofOfDelivery />}
            {!isPickupOnly && user_type !== "shipper" && isPoPSubmitted && !isOffPlatform && (
              <>
                <ProofOfPickup />
                <div className="mt-3xl">
                  <ProofOfDelivery />
                </div>
              </>
            )}
          </form>
        </FormProvider>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex align-items-center justify-content-end">
          <div className="mr-lg">
            <Button
              label={intl.formatMessage({ id: "general.back", defaultMessage: "Back" })}
              onClick={onHide}
              variant="secondary-gray"
            />
          </div>
          <Button
            label={intl.formatMessage({ id: "general.confirm", defaultMessage: "Confirm" })}
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PickupDeliveryProofDialog;

PickupDeliveryProofDialog.defaultProps = {
  setIsShipmentDelivered: undefined,
  refetchFn: undefined,
  mode: "default",
};
